import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import type { BreadcrumbsProps } from './types';

/**
 * `Experience Design › User Research › Field Study`.
 *
 * **It exists because neither the rail nor the back link names the *process* a
 * method belongs to**, which on a method page is the level a reader most wants to
 * step back into.
 *
 * **The last crumb is text, not a link** — it names the page you are on.
 *
 * The `›` is supplied here and `aria-hidden`: `nav` plus `aria-current="page"`
 * carry the structure, and "single right-pointing angle quotation mark" carries
 * nothing.
 */
export const Breadcrumbs = ({ crumbs }: BreadcrumbsProps) => {
  // A trail of one is not a trail. `/sdlc` itself is the case.
  if (crumbs.length < 2) {
    return null;
  }

  return (
    <Box component="nav" aria-label="Breadcrumb">
      <Box
        component="ol"
        sx={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;

          return (
            <Box
              component="li"
              key={crumb.label}
              sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
            >
              {index > 0 && (
                <Typography
                  component="span"
                  variant="body2"
                  aria-hidden
                  sx={{ color: 'text.disabled' }}
                >
                  &rsaquo;
                </Typography>
              )}

              {isLast || crumb.to === undefined ? (
                <Typography
                  component="span"
                  variant="body2"
                  aria-current={isLast ? 'page' : undefined}
                  sx={{ color: 'text.meta' }}
                >
                  {crumb.label}
                </Typography>
              ) : (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ color: 'text.link', '&:hover': { textDecoration: 'underline' } }}
                >
                  <RouterLink to={crumb.to} style={{ color: 'inherit', textDecoration: 'none' }}>
                    {crumb.label}
                  </RouterLink>
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
